// Driver Dashboard — real-time driver app on live Firestore data
// ─────────────────────────────────────────────────────────────
// Presence:   drivers/{uid} doc (status, activeOrderId, stats)
// Offers:     job_offers where driverId == uid && status == 'pending'
// Active job: orders/{activeOrderId} subscription
// All actions go through driver callables (setDriverStatus,
// respondToJobOffer, verifyPickupCode, startDelivery, completeDelivery,
// reportDeliveryIssue, cancelActiveJob). Location is streamed via
// updateDriverLocation while online.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Navigation,
  Package,
  Clock,
  MapPin,
  Phone,
  MessageSquare,
  ChevronRight,
  Power,
  AlertTriangle,
  Loader2,
  Car,
  Bike,
  Settings,
  ExternalLink,
  Timer,
  Wallet,
  CheckCircle2,
  Route,
  Camera,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { PickupForm } from '../../components/driver/PickupForm';
import { CameraView } from '../../components/driver/CameraView';
import { DriverSafetyPanel } from '../../components/driver/DriverSafetyPanel';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

type VehicleType = 'bike' | 'car' | 'scooter';

interface JobOffer {
  id: string;
  orderId: string;
  storeName: string;
  storeAddress: string;
  dropoffAddress: string;
  distanceKm: number;
  itemCount: number;
  baseCents: number;
  tipCents: number;
  totalCents: number;
  expiresAt?: { toMillis: () => number };
}

interface DriverDoc {
  status?: 'offline' | 'online' | 'on_job';
  activeOrderId?: string | null;
  vehicleType?: VehicleType;
  stats?: { deliveries?: number; earningsCents?: number; tipsCents?: number };
}

interface DriverStats {
  today: { deliveries: number; earnings: number; tips: number };
  lifetime: { deliveries: number; earnings: number; tips: number };
}

const LOCATION_INTERVAL_MS = 30_000;

export function DriverDashboard() {
  const { user } = useAuth();
  const uid = user?.uid || null;

  // ── Live data ────────────────────────────────────────────
  const [driverDoc, setDriverDoc] = useState<DriverDoc | null>(null);
  const [driverDocLoaded, setDriverDocLoaded] = useState(false);
  const [offers, setOffers] = useState<JobOffer[]>([]);
  const [activeOrder, setActiveOrder] = useState<any | null>(null);
  const [stats, setStats] = useState<DriverStats | null>(null);

  // ── UI state ─────────────────────────────────────────────
  const [vehicleType, setVehicleType] = useState<VehicleType>('car');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showSafetyPanel, setShowSafetyPanel] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [arrivedAtStore, setArrivedAtStore] = useState(false);
  const [arrivedAtCustomer, setArrivedAtCustomer] = useState(false);
  const [completedEarnings, setCompletedEarnings] = useState<{ base: number; tip: number; total: number } | null>(null);

  const status = driverDoc?.status || 'offline';
  const activeOrderId = driverDoc?.activeOrderId || null;

  // ── Subscriptions ────────────────────────────────────────

  // Driver presence doc
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'drivers', uid), (snap) => {
      setDriverDoc((snap.data() as DriverDoc) || null);
      const vt = snap.data()?.vehicleType;
      if (vt) setVehicleType(vt);
      setDriverDocLoaded(true);
    }, () => setDriverDocLoaded(true));
    return unsub;
  }, [uid]);

  // Pending offers (only useful while online without a job)
  useEffect(() => {
    if (!uid || status !== 'online' || activeOrderId) {
      setOffers([]);
      return;
    }
    const q = query(
      collection(db, 'job_offers'),
      where('driverId', '==', uid),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      const now = Date.now();
      setOffers(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<JobOffer, 'id'>) }))
          .filter((o) => !o.expiresAt || o.expiresAt.toMillis() > now)
      );
    });
    return unsub;
  }, [uid, status, activeOrderId]);

  // Active order
  useEffect(() => {
    if (!activeOrderId) {
      setActiveOrder(null);
      setArrivedAtStore(false);
      setArrivedAtCustomer(false);
      return;
    }
    const unsub = onSnapshot(doc(db, 'orders', activeOrderId), (snap) => {
      setActiveOrder(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return unsub;
  }, [activeOrderId]);

  // Boutique pickup address (orders don't embed it)
  const [pickupAddress, setPickupAddress] = useState<string>('');
  useEffect(() => {
    const businessId = activeOrder?.businessId;
    if (!businessId) {
      setPickupAddress('');
      return;
    }
    getDoc(doc(db, 'businesses', businessId))
      .then((snap) => setPickupAddress(formatAddr(snap.data()?.address)))
      .catch(() => setPickupAddress(''));
  }, [activeOrder?.businessId]);

  // Earnings stats
  const refreshStats = useCallback(async () => {
    try {
      const getStats = httpsCallable(functions, 'getDriverStats');
      const result = await getStats({});
      setStats(result.data as DriverStats);
    } catch {
      // Non-blocking
    }
  }, []);

  useEffect(() => {
    if (uid) void refreshStats();
  }, [uid, refreshStats]);

  // Location streaming while online / on job
  const locationTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!uid || status === 'offline') {
      if (locationTimer.current) clearInterval(locationTimer.current);
      return;
    }

    const sendLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const update = httpsCallable(functions, 'updateDriverLocation');
          void update({ lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 10_000 }
      );
    };

    sendLocation();
    locationTimer.current = setInterval(sendLocation, LOCATION_INTERVAL_MS);
    return () => {
      if (locationTimer.current) clearInterval(locationTimer.current);
    };
  }, [uid, status]);

  // ── Actions ──────────────────────────────────────────────

  const runAction = async (fn: () => Promise<void>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err?.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const goOnline = () =>
    runAction(async () => {
      const location = await new Promise<{ lat: number; lng: number } | undefined>((resolve) => {
        if (!navigator.geolocation) return resolve(undefined);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(undefined),
          { timeout: 8_000 }
        );
      });
      const setStatus = httpsCallable(functions, 'setDriverStatus');
      await setStatus({ status: 'online', vehicleType, location });
    });

  const goOffline = () =>
    runAction(async () => {
      const setStatus = httpsCallable(functions, 'setDriverStatus');
      await setStatus({ status: 'offline' });
    });

  const respondToOffer = (offerId: string, accept: boolean) =>
    runAction(async () => {
      const respond = httpsCallable(functions, 'respondToJobOffer');
      await respond({
        offerId,
        accept,
        clientRequestId: `offer_${offerId}_${accept ? 'a' : 'd'}`,
      });
    });

  const handleVerifyPickup = async (pin: string): Promise<boolean> => {
    if (!activeOrderId) return false;
    const verify = httpsCallable(functions, 'verifyPickupCode');
    const result = await verify({ orderId: activeOrderId, code: pin });
    return (result.data as { success: boolean }).success;
  };

  const handleStartDelivery = () =>
    runAction(async () => {
      const start = httpsCallable(functions, 'startDelivery');
      await start({ orderId: activeOrderId, clientRequestId: `start_${activeOrderId}` });
    });

  const handleCompleteDelivery = async (photoDataUrl: string, note?: string) => {
    if (!activeOrderId) return;
    setBusy(true);
    setActionError(null);
    try {
      // Upload proof photo to Storage, then complete with its URL
      const photoRef = storageRef(storage, `delivery-proofs/${activeOrderId}/${Date.now()}.jpg`);
      await uploadString(photoRef, photoDataUrl, 'data_url');
      const photoUrl = await getDownloadURL(photoRef);

      const complete = httpsCallable(functions, 'completeDelivery');
      const result = await complete({
        orderId: activeOrderId,
        photoUrl,
        notes: note,
        clientRequestId: `complete_${activeOrderId}`,
      });
      const data = result.data as { earnings: { base: number; tip: number; total: number } };
      setCompletedEarnings(data.earnings);
      setShowCamera(false);
      void refreshStats();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to complete delivery.');
      setShowCamera(false);
    } finally {
      setBusy(false);
    }
  };

  const handleReportIssue = (reason: string, details?: string) =>
    runAction(async () => {
      const report = httpsCallable(functions, 'reportDeliveryIssue');
      await report({ orderId: activeOrderId, reason, details });
      setShowSafetyPanel(false);
    });

  const handleCancelJob = () =>
    runAction(async () => {
      const cancel = httpsCallable(functions, 'cancelActiveJob');
      await cancel({ orderId: activeOrderId, reason: 'Driver cancelled before pickup' });
    });

  // ── Loading ──────────────────────────────────────────────
  if (!uid || !driverDocLoaded) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  // ── Completion celebration ───────────────────────────────
  if (completedEarnings) {
    return (
      <CompletionScreen
        earnings={completedEarnings}
        onFindNext={() => setCompletedEarnings(null)}
      />
    );
  }

  // ── Safety panel ─────────────────────────────────────────
  if (showSafetyPanel && activeOrderId) {
    return (
      <DriverSafetyPanel
        orderId={activeOrderId}
        onReport={(reason: string, details?: string) => void handleReportIssue(reason, details)}
        onClose={() => setShowSafetyPanel(false)}
      />
    );
  }

  // ── Camera (delivery proof) ──────────────────────────────
  if (showCamera && activeOrder) {
    return (
      <CameraView
        orderId={activeOrder.id}
        customerName={activeOrder.customerName || 'Customer'}
        onSubmit={(photoUrl, note) => void handleCompleteDelivery(photoUrl, note)}
        onCancel={() => setShowCamera(false)}
      />
    );
  }

  // ── Offline ──────────────────────────────────────────────
  if (status === 'offline') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-purple-950 flex flex-col">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
              <span className="text-white font-serif font-bold">F</span>
            </div>
            <div>
              <p className="text-white font-medium">Driver Mode</p>
              <p className="text-gray-400 text-xs">Off duty</p>
            </div>
          </div>
          <button className="p-2 text-gray-400 hover:text-white transition-colors">
            <Settings className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-32 h-32 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-700"
            >
              <Power className="h-16 w-16 text-gray-600" />
            </motion.div>
            <h1 className="text-3xl font-serif text-white mb-2">You're Offline</h1>
            <p className="text-gray-400 mb-8 max-w-xs mx-auto">
              Go online to start receiving FASH delivery requests
            </p>

            {/* Vehicle selection */}
            <div className="mb-8 space-y-3">
              <p className="text-gray-500 text-sm uppercase tracking-wider">Vehicle</p>
              <div className="flex justify-center space-x-2">
                {(['bike', 'car', 'scooter'] as VehicleType[]).map((vt) => (
                  <button
                    key={vt}
                    onClick={() => setVehicleType(vt)}
                    className={cn(
                      'px-4 py-2 rounded-xl text-sm font-medium transition-all capitalize flex items-center gap-1.5',
                      vehicleType === vt
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    )}
                  >
                    {vt === 'bike' ? <Bike className="h-4 w-4" /> : <Car className="h-4 w-4" />}
                    {vt}
                  </button>
                ))}
              </div>
            </div>

            {actionError && (
              <p className="text-red-400 text-sm mb-4 max-w-xs mx-auto">{actionError}</p>
            )}

            <Button
              size="lg"
              onClick={goOnline}
              disabled={busy}
              className="px-16 py-6 text-lg bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Power className="h-5 w-5 mr-2" />
              )}
              Go Online
            </Button>
          </div>
        </div>

        {/* Today's stats */}
        <div className="bg-gray-800/50 backdrop-blur p-6 border-t border-gray-700">
          <p className="text-gray-400 text-sm font-medium mb-4">Today's Summary</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{stats?.today.deliveries ?? 0}</p>
              <p className="text-xs text-gray-400">Deliveries</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">
                ${(stats?.today.earnings ?? 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400">Earnings</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">
                ${(stats?.today.tips ?? 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400">Tips</p>
            </div>
          </div>
          {stats && stats.lifetime.deliveries > 0 && (
            <p className="text-center text-xs text-gray-500 mt-4">
              Lifetime: {stats.lifetime.deliveries} deliveries · ${stats.lifetime.earnings.toFixed(2)} earned
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Online, waiting / offers ─────────────────────────────
  if (status === 'online' && !activeOrder) {
    const currentOffer = offers[0];

    return (
      <div className="min-h-screen bg-gray-100">
        <div className={cn('relative bg-gradient-to-br from-gray-200 to-gray-300', currentOffer ? 'h-[45vh]' : 'h-[70vh]')}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <Navigation className="h-8 w-8 text-emerald-600" />
              </motion.div>
              <p className="text-gray-600 font-medium">
                {currentOffer ? 'New delivery offer!' : 'Looking for deliveries...'}
              </p>
              <p className="text-gray-400 text-sm">You'll be notified when an offer arrives</p>
            </div>
          </div>

          {/* Online header */}
          <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/40 to-transparent">
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                      <Navigation className="h-6 w-6 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-heading">Online</p>
                    <p className="text-xs text-body">FASH Deliveries</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 bg-gray-100 rounded-full px-3 py-1">
                    {vehicleType === 'bike' ? (
                      <Bike className="h-4 w-4 text-body" />
                    ) : (
                      <Car className="h-4 w-4 text-body" />
                    )}
                    <span className="text-xs font-medium text-body capitalize">{vehicleType}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goOffline}
                    disabled={busy}
                    className="text-error border-error/20 hover:bg-error/5"
                  >
                    Go Offline
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {offers.length > 1 && (
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Package className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-heading">
                    {offers.length} deliveries available
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-body" />
              </div>
            </div>
          )}
        </div>

        {actionError && (
          <div className="mx-4 mt-4 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{actionError}</p>
          </div>
        )}

        {currentOffer && (
          <div className="p-4 -mt-16 relative z-10">
            <LiveJobOfferCard
              key={currentOffer.id}
              offer={currentOffer}
              busy={busy}
              onAccept={() => void respondToOffer(currentOffer.id, true)}
              onDecline={() => void respondToOffer(currentOffer.id, false)}
            />
          </div>
        )}

        {!currentOffer && stats && (
          <div className="p-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-body text-sm">
                    <Wallet className="h-4 w-4" />
                    <span>Today</span>
                  </div>
                  <p className="text-sm">
                    <span className="font-bold text-heading">{stats.today.deliveries}</span>
                    <span className="text-body"> trips · </span>
                    <span className="font-bold text-success">${stats.today.earnings.toFixed(2)}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // ── Active job ───────────────────────────────────────────
  if (!activeOrder) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  const orderState = activeOrder.state as string;
  const items: { name: string; quantity: number }[] = (activeOrder.items || []).map((i: any) => ({
    name: i.name,
    quantity: i.quantity || 1,
  }));
  const storeName = activeOrder.businessName || 'Boutique';
  const storeAddress = pickupAddress || storeName;
  const customerAddress = formatAddr(activeOrder.deliveryAddress);
  const customerName = activeOrder.customerName || 'Customer';
  const customerPhone = activeOrder.customerPhone;

  // Pickup PIN entry
  if (orderState === 'DRIVER_ASSIGNED' && arrivedAtStore) {
    return (
      <PickupForm
        orderId={activeOrder.id}
        storeName={storeName}
        items={items}
        maxAttempts={5}
        onVerify={handleVerifyPickup}
        onContactSupport={() => setShowSafetyPanel(true)}
      />
    );
  }

  const headingToStore = orderState === 'DRIVER_ASSIGNED';
  const pickedUp = orderState === 'PICKED_UP';
  const outForDelivery = orderState === 'OUT_FOR_DELIVERY';
  const destinationAddress = headingToStore ? storeAddress : customerAddress;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destinationAddress)}`;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Map header */}
      <div className="h-[38vh] bg-gradient-to-br from-gray-200 to-gray-300 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Route className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">{headingToStore ? 'Head to the boutique' : 'Head to the customer'}</p>
          </div>
        </div>

        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <div className="inline-flex items-center space-x-1 rounded-full font-medium text-xs px-2 py-0.5 bg-purple-100 text-purple-700">
            <span className="font-bold tracking-wide">FASH</span>
          </div>
          <span className="text-xs bg-white/90 backdrop-blur px-3 py-1 rounded-full font-medium text-heading">
            Order #{activeOrder.id.slice(-6).toUpperCase()}
          </span>
        </div>

        {/* Quick actions */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-center space-x-3">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/95 backdrop-blur rounded-full p-3 shadow-lg"
          >
            <Navigation className="h-5 w-5 text-primary" />
          </a>
          {!headingToStore && customerPhone && (
            <>
              <a href={`tel:${customerPhone}`} className="bg-white/95 backdrop-blur rounded-full p-3 shadow-lg">
                <Phone className="h-5 w-5 text-body" />
              </a>
              <a href={`sms:${customerPhone}`} className="bg-white/95 backdrop-blur rounded-full p-3 shadow-lg">
                <MessageSquare className="h-5 w-5 text-body" />
              </a>
            </>
          )}
        </div>
      </div>

      {/* Job sheet */}
      <div className="bg-white rounded-t-3xl -mt-6 relative z-10 min-h-[62vh]">
        <div className="flex justify-center py-3">
          <div className="w-12 h-1 bg-gray-200 rounded-full" />
        </div>

        <div className="px-4 pb-6">
          {/* Phase header */}
          <div
            className={cn(
              'relative text-center py-4 rounded-2xl mb-4 overflow-hidden',
              headingToStore ? 'bg-blue-50' : 'bg-purple-50'
            )}
          >
            <div className={cn('absolute top-0 left-0 right-0 h-1 bg-purple-500')} />
            <p className="text-sm text-body mb-1">
              {headingToStore && '📍 Heading to pickup'}
              {pickedUp && '📦 Picked up — ready to deliver'}
              {outForDelivery && '🛵 Heading to customer'}
            </p>
            <h2 className="text-xl font-serif text-heading">
              {headingToStore ? storeName : customerName}
            </h2>
          </div>

          {actionError && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{actionError}</p>
            </div>
          )}

          {/* Destination card */}
          <Card className="mb-4 border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      headingToStore ? 'bg-blue-100' : 'bg-purple-100'
                    )}
                  >
                    <MapPin className={cn('h-5 w-5', headingToStore ? 'text-blue-600' : 'text-primary')} />
                  </div>
                  <div>
                    <p className="font-medium text-heading">{destinationAddress}</p>
                    {activeOrder.deliveryNotes && !headingToStore && (
                      <p className="text-sm text-body mt-1">{activeOrder.deliveryNotes}</p>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-primary" asChild>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Maps
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Phase actions */}
          {headingToStore && (
            <div className="space-y-3">
              <Button fullWidth size="lg" onClick={() => setArrivedAtStore(true)}>
                I've Arrived at Store
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={handleCancelJob}
                disabled={busy}
                className="text-body"
              >
                Cancel Job
              </Button>
            </div>
          )}

          {pickedUp && (
            <div className="space-y-3">
              <Button fullWidth size="lg" onClick={handleStartDelivery} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Start Delivery
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowSafetyPanel(true)}
                className="text-body"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Report Issue
              </Button>
            </div>
          )}

          {outForDelivery && !arrivedAtCustomer && (
            <div className="space-y-3">
              <Button fullWidth size="lg" onClick={() => setArrivedAtCustomer(true)}>
                I've Arrived
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowSafetyPanel(true)}
                className="text-body"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Report Issue
              </Button>
            </div>
          )}

          {outForDelivery && arrivedAtCustomer && (
            <div className="space-y-3">
              <Button fullWidth size="lg" onClick={() => setShowCamera(true)} disabled={busy}>
                <Camera className="h-5 w-5 mr-2" />
                Complete Delivery
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowSafetyPanel(true)}
                className="text-error border-error/20"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Report Issue
              </Button>
            </div>
          )}

          {/* Order contents */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-heading">
                {items.reduce((n, i) => n + i.quantity, 0)} item(s)
              </p>
              <div className="text-right">
                <p className="text-lg font-bold text-success">
                  $
                  {(
                    ((activeOrder.pricing?.deliveryFeeCents ?? 0) +
                      (activeOrder.pricing?.tipCents ?? 0)) / 100
                  ).toFixed(2)}
                </p>
                <p className="text-xs text-body">Your earnings</p>
              </div>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm text-body">
                  <span>{item.name}</span>
                  <span>×{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Live job offer card with countdown ───────────────────────

function LiveJobOfferCard({
  offer,
  busy,
  onAccept,
  onDecline,
}: {
  offer: JobOffer;
  busy: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const expiresAtMs = offer.expiresAt?.toMillis() ?? Date.now() + 120_000;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeLeft = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
  const timerProgress = Math.min(1, timeLeft / 120);
  const isUrgent = timeLeft < 30;
  const total = offer.totalCents / 100;

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white rounded-3xl shadow-2xl overflow-hidden"
    >
      <div className="relative h-1.5 bg-gray-100">
        <div
          className={cn('h-full transition-all duration-1000', isUrgent ? 'bg-amber-500' : 'bg-primary')}
          style={{ width: `${timerProgress * 100}%` }}
        />
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center space-x-1 rounded-full font-medium text-xs px-2 py-0.5 bg-purple-100 text-purple-700 mb-1">
              <span className="font-bold tracking-wide">FASH</span>
            </div>
            <h3 className="text-xl font-serif text-heading">{offer.storeName}</h3>
            <p className="text-xs text-body mt-1 flex items-center">
              <Timer className="h-3 w-3 mr-1" />
              {offer.distanceKm.toFixed(1)} km from you
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-success">${total.toFixed(2)}</p>
            <p className="text-xs text-body">{offer.itemCount} item(s)</p>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-heading">Pickup</p>
              <p className="text-sm text-body">{offer.storeAddress || offer.storeName}</p>
            </div>
          </div>

          <div className="ml-4 border-l-2 border-dashed border-gray-200 h-4" />

          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-heading">Dropoff</p>
              <p className="text-sm text-body">{offer.dropoffAddress}</p>
            </div>
          </div>
        </div>

        {/* Earnings breakdown */}
        <div className="bg-emerald-50 rounded-xl p-3 mb-5 border border-emerald-100">
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-emerald-700">Delivery fee</span>
              <span className="text-emerald-900">${(offer.baseCents / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-emerald-700">Tip</span>
              <span className="text-emerald-900">${(offer.tipCents / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-1 border-t border-emerald-200 mt-1">
              <span className="text-emerald-800">Total</span>
              <span className="text-emerald-900">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-xl mb-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-body" />
            <span className="text-sm font-medium text-heading">Offer expires in</span>
          </div>
          <span className={cn('text-sm font-bold', isUrgent ? 'text-amber-500' : 'text-primary')}>
            {timeLeft}s
          </span>
        </div>

        <Button fullWidth size="lg" onClick={onAccept} disabled={busy || timeLeft === 0}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Accept Delivery
        </Button>

        <button
          onClick={onDecline}
          disabled={busy}
          className="w-full mt-3 py-3 text-sm font-medium text-body hover:text-heading transition-colors"
        >
          Decline
        </button>
      </div>
    </motion.div>
  );
}

// ─── Completion screen ────────────────────────────────────────

function CompletionScreen({
  earnings,
  onFindNext,
}: {
  earnings: { base: number; tip: number; total: number };
  onFindNext: () => void;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8 max-w-sm w-full"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle2 className="h-12 w-12 text-white" />
        </motion.div>

        <h3 className="text-2xl font-serif text-heading mb-6">Delivery Complete!</h3>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-emerald-50 rounded-2xl p-6 mb-6"
        >
          <p className="text-emerald-700 text-sm mb-1">You earned</p>
          <p className="text-4xl font-bold text-emerald-600 mb-3">${earnings.total.toFixed(2)}</p>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-emerald-600">Delivery fee</span>
              <span className="text-emerald-800">${earnings.base.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-600">Tip</span>
              <span className="text-emerald-800">${earnings.tip.toFixed(2)}</span>
            </div>
          </div>
        </motion.div>

        <Button fullWidth size="lg" onClick={onFindNext} className="bg-gradient-to-r from-purple-500 to-purple-600">
          <Navigation className="h-5 w-5 mr-2" />
          Find Next Delivery
        </Button>
      </motion.div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────

function formatAddr(addr: any): string {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return [
    addr.street || addr.line1 || '',
    addr.apt ? `Apt ${addr.apt}` : '',
    addr.city || '',
    addr.state || '',
    addr.zipCode || addr.zip || '',
  ]
    .filter(Boolean)
    .join(', ');
}
